/**
 * ONE RUN PER BURST.
 *
 * A live page that answers an event by re-reading must not re-read once per
 * event: a review close accepts a dozen items in one pass, a bulk close
 * rejects a meeting's worth of assignments, a pipeline run lands several
 * notices together. `coalesce(run, waitMs)` returns a function that, asked
 * any number of times, runs `run` ONCE, `waitMs` after the FIRST ask of the
 * burst. The wait is fixed from that first ask (not restarted by each later
 * one, as a debounce would be), so a steady stream of events can delay a
 * re-read by at most `waitMs`, never starve it.
 *
 * `cancel()` drops a run still waiting — a page that is left, or a sign-out,
 * must not re-read into whatever comes next. A `run` that throws is logged,
 * never thrown out of a timer.
 */
export interface Coalesced {
  /** Ask for a run: the first ask of a burst starts the wait, later ones join it. */
  (): void;
  /** Drop a run still waiting. Idempotent. */
  cancel(): void;
}

export function coalesce(run: () => void, waitMs: number): Coalesced {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const ask = (() => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      try {
        run();
      } catch (err) {
        console.error("coalesce: a re-run threw", err);
      }
    }, waitMs);
  }) as Coalesced;
  ask.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return ask;
}
